import { LISTS_DETAIL_PATH } from '@/app/constants';
import type { SavedCustomList, SavedListResponse } from '@/features/types';
import {
  useClientTerminology,
  type ClientTerminology,
} from '@/views/list/NetworkTerms';
import { Anchor } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { captureException } from '@sentry/react';
import { useCallback } from 'react';
import { Check, X } from 'react-feather';
import { Link, generatePath } from 'react-router-dom';
import { useAppDispatch } from '@/app/hooks';
import { incrementMvtCacheCounter } from '@/features/analysisMapSlice';

interface NotificationValues {
  readonly count: number;
  readonly listName: string;
  a: (chunks: React.ReactNode) => React.ReactNode;
  [key: string]:
    | string
    | number
    | ((chunks: React.ReactNode) => React.ReactNode);
}

interface NotificationMessages {
  loadingTitle?: string;
  loadingMessage?: string;
  successTitle?: string;
  successMessage?: string;
  errorTitle?: string;
  errorMessage?: string;
}

function showLoadingNotification(
  intl: ClientTerminology,
  messages?: NotificationMessages
) {
  return notifications.show({
    loading: true,
    title:
      messages?.loadingTitle ||
      intl.formatMessage({ id: 'customList.addingToList' }),
    message:
      messages?.loadingMessage ||
      intl.formatMessage({ id: 'customList.addingToListMessage' }),
    autoClose: false,
    withCloseButton: false,
  });
}

function updateNotification({
  id,
  intl,
  values,
  isSuccess,
  messages,
}: {
  id: string;
  intl: ClientTerminology;
  values: NotificationValues;
  isSuccess: boolean;
  messages?: NotificationMessages;
}) {
  const defaultSuccessTitle = intl.formatMessage(
    { id: 'customList.addedSpansToListTitle' },
    values
  );
  const defaultSuccessMessage = intl.formatMessage(
    { id: 'customList.spansAddedToListDescription' },
    values
  );
  const defaultErrorTitle = intl.formatMessage({
    id: 'customList.errorAddingToList',
  });
  const defaultErrorMessage = intl.formatMessage({
    id: 'customList.errorAddingToList',
  });

  notifications.update({
    id,
    color: isSuccess ? 'green' : 'red',
    title: isSuccess
      ? messages?.successTitle || defaultSuccessTitle
      : messages?.errorTitle || defaultErrorTitle,
    message: isSuccess
      ? messages?.successMessage || defaultSuccessMessage
      : messages?.errorMessage || defaultErrorMessage,
    icon: isSuccess ? <Check /> : <X />,
    loading: false,
    autoClose: 2000,
  });
}
/** Utility to generate the anchor link for notifications */
function getListAnchor(
  listId: string,
  chunks: React.ReactNode
): React.ReactNode {
  return (
    <Anchor
      component={Link}
      to={generatePath(LISTS_DETAIL_PATH, {
        id: listId,
      })}
    >
      {chunks}
    </Anchor>
  );
}
/** Utility to generate notification values object for runListAction */
function notificationValues(summary?: SavedCustomList) {
  return {
    get count() {
      return summary?.modified ?? summary?.total ?? 0;
    },
    get listName() {
      return summary?.name ?? '';
    },
    a: (chunks: React.ReactNode) => getListAnchor(summary?.id ?? '', chunks),
  };
}

/**
 * Hook for running list-related actions with consistent UX patterns.
 *
 * This hook provides standardized behavior for list operations including:
 * - Automatic loading notifications while the action is running
 * - Consistent success/error notifications with proper messaging
 * - Automatic modal closure when the action starts
 * - Error handling with Sentry integration
 * - Notification links that navigate to the affected list
 *
 * Use this hook for any operation that:
 * - Creates a new custom list
 * - Adds items (spans) to existing lists
 */
export const useRunListAction = (
  onUpdateStart: () => void,
  messages?: NotificationMessages
) => {
  const intl = useClientTerminology();
  const dispatch = useAppDispatch();

  const runListAction = useCallback(
    async (asyncAction: () => Promise<SavedListResponse>) => {
      const notificationId = showLoadingNotification(intl, messages);
      try {
        onUpdateStart();
        const updatedList = await asyncAction();
        updateNotification({
          id: notificationId,
          intl,
          values: notificationValues(updatedList.summary),
          isSuccess: true,
          messages,
        });
        dispatch(incrementMvtCacheCounter());

        return updatedList;
      } catch (error) {
        captureException(error);
        updateNotification({
          id: notificationId,
          intl,
          values: notificationValues(),
          isSuccess: false,
          messages,
        });
        return undefined;
      }
    },
    [intl, onUpdateStart, messages, dispatch]
  );

  return runListAction;
};
